import { useEffect } from 'react';
import { useParams, Link, useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { fetchPokemonById } from '../store/pokemonSlice';
import './PokemonDetail.css';

function PokemonDetail() {
  const { id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const numericId = parseInt(id);
  
  const pokemon = useSelector(state => state.pokemon.pokemonDetails[id]);
  const loading = useSelector(state => state.pokemon.loading[id]);
  const error = useSelector(state => state.pokemon.error[id]);

  useEffect(() => {
    if (!pokemon) {
      dispatch(fetchPokemonById(id));
    }
  }, [dispatch, id, pokemon]);

  const handleNavigation = (direction) => {
    const newId = direction === 'prev' ? numericId - 1 : numericId + 1;
    if (newId >= 1 && newId <= 1025) { // 1025 is the current max Pokemon number as of Mochi Mayhem DLC
      navigate(`/pokemon/${newId}`);
    }
  };

  const handleBackToPokedex = () => {
    navigate('/database');
  };

  const renderContent = () => {
    if (error) return <div className="error-container">Error: {error}</div>;
    
    // Calculate if we should show shiny sprite (1/4096 chance)
    const shouldShowShiny = Math.floor(Math.random() * 4096) === 0;
    
    return (
      <div className="pokemon-detail__content">
        {/* Top Left Quadrant - Sprite and Types */}
        <div className="pokemon-detail__quadrant">
          <div className="pokemon-detail__sprite-section">
            <div className="pokemon-detail__image-container">
              {pokemon && (
                <img
                  src={shouldShowShiny ? 
                    (pokemon.sprites?.other?.home?.front_shiny || 
                     pokemon.sprites?.frontShiny || 
                     `https://raw.githubusercontent.com/PokeAPI/sprites/master/sprites/pokemon/shiny/${pokemon.pokedexId}.png`) :
                    (pokemon.sprites?.other?.['official-artwork']?.front_default || 
                     pokemon.sprites?.frontDefault ||
                     `https://raw.githubusercontent.com/PokeAPI/sprites/master/sprites/pokemon/other/official-artwork/${pokemon.pokedexId}.png`)}
                  alt={`${pokemon?.name}${shouldShowShiny ? ' (Shiny)' : ''}`}
                  className="pokemon-detail__image"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = shouldShowShiny ?
                      `https://raw.githubusercontent.com/PokeAPI/sprites/master/sprites/pokemon/shiny/${pokemon.pokedexId}.png` :
                      `https://raw.githubusercontent.com/PokeAPI/sprites/master/sprites/pokemon/${pokemon.pokedexId}.png`;
                    e.target.onerror = () => {
                      e.target.src = '/placeholder-pokemon.png';
                      e.target.onerror = null;
                    };
                  }}
                />
              )}
            </div>
            <div className="pokemon-detail__types">
              {pokemon?.types.map(type => (
                <span key={type} className={`pokemon-detail__type pokemon-detail__type--${type}`}>
                  {type}
                </span>
              ))}
            </div>
          </div>
        </div>

        {/* Top Right Quadrant - Physical Stats and Abilities */}
        <div className="pokemon-detail__quadrant">
          {pokemon && (
            <>
              <div className="pokemon-detail__physical">
                <div className="pokemon-detail__physical-stat">
                  <span>Height</span>
                  <strong>{(pokemon.height / 10).toFixed(1)} m</strong>
                </div>
                <div className="pokemon-detail__physical-stat">
                  <span>Weight</span>
                  <strong>{(pokemon.weight / 10).toFixed(1)} kg</strong>
                </div>
                <div className="pokemon-detail__physical-stat">
                  <span>Catch Rate</span>
                  <strong>{pokemon.capture_rate}</strong>
                  <div className="tooltip">
                    Base catch rate value (0-255). Higher numbers mean easier to catch. This is used in a formula along with the Pokémon's HP, status conditions, and Poké Ball type to determine the final catch probability.
                  </div>
                </div>
              </div>

              <div className="pokemon-detail__abilities">
                <h2>Abilities</h2>
                <div className="pokemon-detail__abilities-list">
                  {pokemon.abilities.map((ability, index) => (
                    <Link 
                      key={`${ability.name}-${index}`} 
                      to={`/abilities/${ability.name.replace(/\s+/g, '-')}`}
                      className="pokemon-detail__ability"
                    >
                      <span className="pokemon-detail__ability-name">
                        {ability.name.split(' ').map(word => 
                          word.charAt(0).toUpperCase() + word.slice(1)
                        ).join(' ')}
                      </span>
                      {ability.isHidden && <span className="pokemon-detail__ability-hidden">(Hidden)</span>}
                      {ability.percentage && (
                        <span className="pokemon-detail__ability-usage">
                          {ability.percentage.toFixed(1)}%
                        </span>
                      )}
                    </Link>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>

        {/* Bottom Left Quadrant - Base Stats */}
        <div className="pokemon-detail__quadrant">
          {pokemon && (
            <div className="pokemon-detail__stats">
              <h2>Base Stats</h2>
              {pokemon.stats.map(stat => (
                <div key={stat.name} className="pokemon-detail__stat">
                  <span className="pokemon-detail__stat-name">
                    {stat.name === 'hp' ? 'HP' :
                     stat.name === 'attack' ? 'Attack' :
                     stat.name === 'defense' ? 'Defense' :
                     stat.name === 'special-attack' ? 'Sp. Atk' :
                     stat.name === 'special-defense' ? 'Sp. Def' :
                     'Speed'}
                  </span>
                  <div className="pokemon-detail__stat-bar">
                    <div
                      className="pokemon-detail__stat-bar-fill"
                      style={{ width: `${(stat.base / 255) * 100}%` }}
                    />
                  </div>
                  <span className="pokemon-detail__stat-value">{stat.base}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Bottom Right Quadrant - Flavor Text */}
        <div className="pokemon-detail__quadrant">
          {pokemon && (
            <div className="pokemon-detail__description">
              <p>{flavorText}</p>
            </div>
          )}
        </div>
      </div>
    );
  };

  // Get the English flavor text
  const flavorText = pokemon?.flavor_text_entries.find(entry => entry.language.name === 'en')?.flavor_text
    .replace(/[\u2190-\u2195]/g, '') // Remove arrows
    .replace(/[\u25B6\u25C0]/g, '') // Remove play/pause symbols
    .replace(/\s+/g, ' ') // Replace multiple spaces with single space
    .trim() || '';

  return (
    <div className="pokemon-detail">
      <div className="led-red"></div>
      <div className="led-yellow"></div>
      <div className="led-green"></div>
      <div className="pokemon-detail__header">
        <div className="pokemon-detail__title">
          <h1>{pokemon?.name || "Loading..."}</h1>
          <span className="pokemon-detail__number">
            #{String(pokemon?.pokedexId || id).padStart(3, '0')}
          </span>
        </div>
        <div className="pokemon-detail__nav-buttons">
          <button onClick={handleBackToPokedex} className="back-to-pokedex" aria-label="Back to Pokédex">
          </button>
          <div className="nav-buttons-container">
            <button 
              className="nav-button prev-button"
              onClick={() => handleNavigation('prev')}
              disabled={numericId <= 1}
              aria-label="Previous Pokémon"
            >
              <div className="d-pad-arrow"></div>
            </button>
            <button 
              className="nav-button next-button"
              onClick={() => handleNavigation('next')}
              disabled={numericId >= 1025}
              aria-label="Next Pokémon"
            >
              <div className="d-pad-arrow"></div>
            </button>
          </div>
        </div>
      </div>

      {renderContent()}
    </div>
  );
}

export default PokemonDetail; 